'use client';

import {
  useState,
} from 'react';

import { getCookie } from '../utils/getCookie';
import {
  TokenAuthenticationHttpClientTData, TokenAuthenticationResponse,
} from '../types/api';

import {
  WagContentApiEndpoint,
  WAG_CONTENT_API_URL,
  SessionStorageKey,
  HttpHeaderKey,
} from '../constants';
import { CookieKey } from '../constants/cookie-key';
import { executionEnvironment } from '../wag-react/common/xenv';
import { httpPost } from '../wag-react/common/api';

type Props = {
  isAnonymous?: boolean;
  username?: string;
  password?: string;
};

const storeAuthToken = (token: string) => {
  if (executionEnvironment.isSSR) {
    return;
  }

  window.sessionStorage.setItem(SessionStorageKey.AuthToken, token);
};

export const getItemFromSessionStorage = (key) => {
  if (executionEnvironment.isSSR) {
    return null;
  }

  return window.sessionStorage.getItem(key);
};

const getAuthToken = () => getItemFromSessionStorage(SessionStorageKey.AuthToken);

export const postAuthToken = async (
  isAnonymous,
) => {
  const data = await httpPost<unknown, TokenAuthenticationResponse>(
    WagContentApiEndpoint.PostTokenAuth,
    {
      anonymous: isAnonymous,
    },
    {
      baseURL: WAG_CONTENT_API_URL,
      headers: {
        [HttpHeaderKey.XCsrfToken]: getCookie(CookieKey.CsrfToken),
      },
    },
  );

  return data;
};

export const usePostAuthToken = ({
  isAnonymous = false,
}: Props) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState(null);

  const execute = async () => {
    const authToken = getAuthToken();

    if (authToken) {
      return authToken;
    }

    setIsLoading(true);

    try {
      const result: TokenAuthenticationHttpClientTData = await postAuthToken(isAnonymous);
      const authenticationToken = result.data.token;
      setIsLoading(false);
      setIsSuccess(true);
      setToken(authenticationToken);
      storeAuthToken(authenticationToken);
      return authenticationToken;
    } catch (_error) {
      setError(_error);
      setIsLoading(false);
      return _error;
    }
  };

  return {
    execute,
    isLoading,
    storeAuthToken,
    isSuccess,
    error,
    data: token,
  };
};
